import type { AiAuthorizationError } from '@lumapps/ai/types';
import type { AskAiRule } from '@lumapps/ask-ai-rules/types';
import { DetailedDirectoryEntryBlockProps } from '@lumapps/directories-entries/types';
import type { StreamUpdate } from '@lumapps/streaming/types';
import { GLOBAL } from '@lumapps/translations';
import { User } from '@lumapps/user/types';

import { ASK_AI_CHAT_STREAMING_STATUS } from './constants';

/** Parameters to give the "ask/search" endpoint */
export interface AskSearchParams {
    /** The query to search for */
    query: string;
    /** The id on which to search */
    siteIds: string[];
    /**
     * The languages to search in.
     * Will be sent as Accepted-languages header
     */
    acceptLanguageHeader?: string;
    /**
     * Unique ID to identify each search.
     * This ID can be used to link multiple search queries together (ex: omnisearch and ask ai)
     */
    traceIdHeader?: string;
    /** Optional agent Id */
    agentId?: string;
    /** Optional metadata IDs to filter results */
    metadataIds?: string[];
}

export enum AskAiDocumentTypes {
    POST = 'post',
    CONTENT = 'content',
    COMMUNITY = 'community',
    DIRECTORY_ENTRY = 'directoryEntry',
    DIRECTORY_ENTRY_LINK = 'directoryentry_link',
    DIRECTORY_ENTRY_MICROAPP = 'directoryentry_microapp',
    MEDIA = 'media',
    USER = 'user',
    GENERIC_DOCUMENT = 'genericDocument',
}

/**
 * Mapping all document types to standard translation key
 * Used as fallback titles in case Ask AI Sources would be returned untitled by the API
 */
export const MAPPING_DOCUMENT_TYPES_TO_DEFAULT_TITLES: Record<AskAiDocumentTypes, string> = {
    [AskAiDocumentTypes.POST]: GLOBAL.POST,
    [AskAiDocumentTypes.CONTENT]: GLOBAL.CONTENT,
    [AskAiDocumentTypes.COMMUNITY]: GLOBAL.COMMUNITY,
    [AskAiDocumentTypes.DIRECTORY_ENTRY]: GLOBAL.DIRECTORY_ENTRY,
    [AskAiDocumentTypes.DIRECTORY_ENTRY_LINK]: GLOBAL.DIRECTORY_ENTRY,
    [AskAiDocumentTypes.DIRECTORY_ENTRY_MICROAPP]: GLOBAL.DIRECTORY_ENTRY,
    [AskAiDocumentTypes.MEDIA]: GLOBAL.MEDIA,
    [AskAiDocumentTypes.USER]: GLOBAL.USER,
    [AskAiDocumentTypes.GENERIC_DOCUMENT]: 'MS_TYPES_DOCUMENT',
};

export enum AskAiDirectoryEntryType {
    DIRECTORY_ENTRY_LINK = 'directoryentry_link',
    DIRECTORY_ENTRY_MICROAPP = 'directoryentry_microapp',
}

export interface AskAiSource {
    /** id of the source */
    id: string;
    /** order the source in frontend */
    sourceIndex: number;
    /** title of the source */
    title: string;
    /** absolute URL needed even for LumApps content */
    url: string;
    /** type of document */
    type: AskAiDocumentTypes;
    // Si generic Document alors on a besoin d'un mimeType
    mimeType?: string;
    /** Used for custom content type contents to have their custom icon */
    icon?: string;
}

/** Best answer for image */
export interface ImageBestAnswer {
    /** url of the image */
    url: string;
    /** title of the image */
    title: string;
    /** description of the image */
    description?: string;
    /** alt text for the image */
    altText?: string;
}

/** Best answer for directory entry */
export interface DirectoryEntryBestAnswer {
    /** title of the directory entry */
    name: string;
    /** microapp or link */
    directoryEntryType: AskAiDirectoryEntryType;
    /** directory entry id */
    id: string;
    /** parent directory id */
    directoryId: string;
    /** link of the directory entry */
    url: string;
    /** resource id of the microapp */
    resourceId?: string;
    /** thumbnail of the directory entry */
    thumbnail?: string;
    /** mostly used for description */
    description?: string;
    /** tags of the entry */
    tags?: DetailedDirectoryEntryBlockProps['tags'];
    /** metadata of the entry */
    metadata?: DetailedDirectoryEntryBlockProps['metadata'];
}

/** Types of actionable item that can be used in a rule */
export enum ACTIONABLE_ITEM_TYPES {
    MICRO_APP = 'micro_app',
    USER_CARD = 'user',
    IMAGES = 'images',
    DIRECTORY_ENTRY = 'directory_entry',
}

export interface MicroAppActionableItemPayload {
    type: ACTIONABLE_ITEM_TYPES.MICRO_APP;
    payload: {
        id: string;
    };
}

export interface UserCardActionableItemPayload {
    type: ACTIONABLE_ITEM_TYPES.USER_CARD;
    payload: User;
}
export interface DirectoryEntryActionableItemPayload {
    type: ACTIONABLE_ITEM_TYPES.DIRECTORY_ENTRY;
    payload: DirectoryEntryBestAnswer;
}

/** payload for image actionable item */
export interface ImagesActionableItemPayload {
    type: ACTIONABLE_ITEM_TYPES.IMAGES;
    payload: { images: ImageBestAnswer[] };
}

export type ActionableItemPayload =
    | MicroAppActionableItemPayload
    | UserCardActionableItemPayload
    | ImagesActionableItemPayload
    | DirectoryEntryActionableItemPayload;

/**
 * Answer given by the "ask/search" endpoint.
 */
export interface AskSearchBestAnswer {
    /** The description of the best answer */
    answer?: string;
    /** query used for the answer */
    query?: string;
    /** whether the answer is a rule set by human moderator */
    isHumanCurated?: boolean;
    /** Payload for the actionableItem to display */
    actionableItem?: ActionableItemPayload;
    /** list of the sources use to generate the response */
    sources?: AskAiSource[];
    /** List of authorization errors */
    authErrors?: AiAuthorizationError[];
    /** Questions that might interest the user based on their query */
    suggestedFollowUpQuestions?: string[];
    /** Id of the current conversation used to keep context between successive answers */
    conversationId?: string;
    /** Title and Id of the rule that matched the query */
    matchedRule?: Pick<AskAiRule, 'title' | 'id'>;
}

export interface EmptyStates {
    /** Icon used for the empty state */
    icon: string;
    /** Subtitle displayed below the icon */
    subtitle: string;
    /** description used for the icon */
    description: string;
}

/** Parameters to give the "ask/chat" endpoint */
export interface AskChatParams {
    /** The query to search for */
    query: string;
    /** The id on which to search */
    siteIds: string[];
    /**
     * The languages to search in.
     * Will be sent as Accepted-languages header
     */
    acceptLanguageHeader?: string;
    /**
     * Unique ID to identify each search.
     * This ID can be used to link multiple search queries together (ex: omnisearch and ask ai)
     */
    traceIdHeader?: string;
    /**
     * ID used to keep context between successive queries hence enabling the conversational experience
     */
    conversationId?: string;
    /**
     * Agent ID - rules are used as agents for now
     */
    agentId?: string;
    /** Optional metadata IDs to filter results */
    metadataIds?: string[];
}

/** The event data received during an Ask AI chat stream */
export interface AskAiChatStreamUpdate extends StreamUpdate {
    /** Status of the chat stream (of the current event) */
    status: ASK_AI_CHAT_STREAMING_STATUS;
}

/** Event data of the `stream_answers` events received during an Ask AI chat stream */
export interface AskAiChatStreamUpdateAnswer extends AskAiChatStreamUpdate {
    answerChunk: string;
}

/** Event data of the `done` event received during an Ask AI chat stream */
export interface AskAiChatStreamUpdateDone extends AskAiChatStreamUpdate, AskSearchBestAnswer {}

/** Features of Ask AI */
export interface AskAiFeatures {
    isConversationalEnabled: boolean;
    isAgentEnabled: boolean;
}
